import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    const parsedUser = savedUser ? JSON.parse(savedUser) : null;
    console.log('🚀 AuthContext: Initializing with user from localStorage:', parsedUser);
    return parsedUser;
  });
  const [token, setToken] = useState(() => {
    const savedToken = localStorage.getItem('token');
    console.log('🚀 AuthContext: Initializing with token:', savedToken ? 'Present' : 'None');
    if (savedToken) {
      // Poser le header immédiatement (avant le premier rendu) pour éviter les requêtes sans token
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
    }
    return savedToken;
  });
  const [loading, setLoading] = useState(true);

  // Utilisation de la variable d'environnement pour l'URL backend
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const API = `${BACKEND_URL}/api`;
  
  // Log pour diagnostic
  console.log('🔐 AuthContext - Backend URL:', BACKEND_URL);

  // Surveiller les changements d'état utilisateur
  useEffect(() => {
    console.log('👤 AuthContext: User state changed:', { 
      user: user ? { username: user.username, role: user.role, id: user.id } : 'null',
      isAuthenticated: !!token && !!user 
    });
    
    // Forcer la mise à jour du contexte si nécessaire
    if (user && token && !user.role && user.username === 'admin') {
      console.log('🔄 AuthContext: Force updating admin role...');
      const updatedUser = { ...user, role: 'super_admin' };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  }, [user, token]);

  // Configure axios interceptor
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check authentication status on app load
  useEffect(() => {
    const checkAuth = async () => {
      console.log('🔍 AuthContext: Checking auth status...', { 
        token: token ? 'Present' : 'None', 
        savedUser: user ? user.username : 'No user'  
      });
      
      if (token && user) {
        try {
          // Tentative simple de validation du token
          const response = await axios.get(`${API}/auth/me`);
          console.log('✅ AuthContext: Token valid, user data:', {
            username: response.data.username,
            role: response.data.role
          });
          
          // Mettre à jour les données utilisateur si nécessaire
          if (response.data.role !== user.role) {
            console.log('🔄 AuthContext: Updating user role from', user.role, 'to', response.data.role);
            const updatedUser = { ...response.data };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
          }
        } catch (error) {
          console.error('❌ AuthContext: Token validation failed:', error.response?.status);
          // Si le token est invalide, déconnecter
          if (error.response?.status === 401 || error.response?.status === 403) {
            console.log('🚪 AuthContext: Auto-logout due to invalid token');
            logout();
          }
        }
      } else if (token && !user) {
        // Si on a un token mais pas d'utilisateur, essayer de récupérer les infos
        try {
          const response = await axios.get(`${API}/auth/me`);
          console.log('✅ AuthContext: Retrieved user data from token');
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (error) {
          console.error('❌ AuthContext: Failed to get user from token');
          logout();
        }
      }
      
      setLoading(false);
    };

    checkAuth();
  }, []); // Seulement au montage du composant

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });

      const { access_token, user: userData } = response.data;
      
      console.log('✅ AuthContext: Login successful, setting user data:', userData);
      
      // Poser le header axios de façon SYNCHRONE avant tout re-render
      // (le useEffect [token] s'exécute après le rendu : les chargements immédiats partaient sans token → 403)
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // S'assurer que le rôle est présent et persisté
      if (!userData.role) {
        console.warn('⚠️ AuthContext: Role missing in login response, setting default');
        userData.role = 'super_admin'; // Pour l'utilisateur admin
      }
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      console.log('✅ AuthContext: User state updated, role:', userData.role);
      console.log('✅ AuthContext: User object in state:', userData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Erreur de connexion' 
      };
    }
  };

  const register = async (userData) => {
    try {
      // Utiliser le nouveau endpoint /register pour l'auto-inscription
      const response = await axios.post(`${API}/register`, userData);
      
      // Après inscription, connecter automatiquement l'utilisateur
      return await login(userData.username, userData.password);
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Erreur lors de l\'inscription' 
      };
    }
  };

  const logout = () => {
    console.log('🚪 AuthContext: Logout called');
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedCompanyId');
    delete axios.defaults.headers.common['Authorization'];
  };
  
  // Fonction de debug pour forcer un reset complet
  const forceReset = () => {
    console.log('🔄 AuthContext: Force reset called');
    localStorage.clear(); // Nettoyer tout le localStorage
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    setLoading(false);
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    forceReset, // Ajouter la fonction de reset
    loading,
    isAuthenticated: !!token && !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};